// documentPanel.js
import React from 'react';
import {useTranslation} from 'react-i18next';
import {DownloadOutlined, MoreOutlined} from '@ant-design/icons';
import {Button, Dropdown, message} from 'antd';
import {download_document_version} from "../../../Services/documents.service";
import AnalyseCreateDropzone from "../create/AnalyseCreateDropzone";
import Document from "../document/document";
import {useSelector} from "react-redux";
import {
    buildAcceptAttributeFromFormats,
    getAllowedUploadFormats,
} from "../../../utils/commercialCapabilities";
import './ReviewPage.css'

const DocumentPanel = ({
                           isCreate,
                           isProcessing,
                           isAnalyzing,
                           isGlobalLoading,
                           content,
                           currentVersion,
                           hoveredRef,
                           selectedRef,
                           normalizedComments,
                           onClauseClick,
                           onClauseHover,
                           onFileSelected,
                           forcedActiveTab,
                           comparisonMode = false,
                           citationHighlights = [],
                           includeCitationHighlights = false,
                           activeDocumentSource = 'contract',
                           onDocumentSourceChange,
                           referenceSource = null,
                           referenceNavigationRequest = null,
                           comparisonVersionSource = null,
                           comparisonVersionNavigationRequest = null,
                           contractNavigationRequest = null,
                   }) => {
    const {t} = useTranslation('review');
    const commercialCapabilities = useSelector((state) => state.commercialControl?.capabilities || {});
    const allowedUploadFormats = getAllowedUploadFormats(commercialCapabilities) || ["pdf"];
    const createAccept = buildAcceptAttributeFromFormats(allowedUploadFormats) || ".pdf";

    // DEBUG: Force display extraction loader
    const forceExtractionLoader = false; // Set to true to preview
    const showExtractionLoader = isProcessing || forceExtractionLoader;

    const hasViewerUrl = !!content?.view_url;

    const downloadVersion = (id, fileName) => {
        download_document_version(id, fileName).catch(() => {
            message.error(t('download.error'));
        });
    };
    const showOverlay = activeDocumentSource === 'contract' && !isCreate && (
        isAnalyzing ||
        isGlobalLoading
    );

    // Bouton download à passer aux tabs
    const headerActions = hasViewerUrl ? (
        <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="doc-actions-dropdown"
            menu={{
                items: [
                    {
                        key: 'download-local',
                        icon: <DownloadOutlined />,
                        label: t('download.local'),
                        onClick: () => downloadVersion(currentVersion, content.title),
                    },
                ],
            }}
        >
            <Button
                type="text"
                className="doc-actions-trigger"
                icon={<MoreOutlined />}
                aria-label={t('download.actions')}
            />
        </Dropdown>
    ) : null;

    // Badge AI processing — minimaliste
    const processingBadge = showExtractionLoader ? (
        <div className="extraction-badge" role="status" aria-live="polite">
            <span className="extraction-badge__spinner" aria-hidden="true" />
            <span className="extraction-badge__label">
                {t('stage.ai_analyzing')}
            </span>
        </div>
    ) : null;

    return (
        <>
            {isCreate ? (
                <AnalyseCreateDropzone
                    disabled={isGlobalLoading}
                    onFileSelected={onFileSelected}
                    accept={createAccept}
                    allowedFormats={allowedUploadFormats}
                    maxSizeMb={50}
                />
            ) : (
                <div style={{position: "relative", height: "100%", flex: 1, display: 'flex', flexDirection: 'column'}}>
                    <Document
                        onClauseClick={onClauseClick}
                        onClauseHover={onClauseHover}
                        content={content}
                        currentVersion={currentVersion}
                        hoveredRef={hoveredRef}
                        selectedRef={selectedRef}
                        commentRefs={comparisonMode ? [] : (normalizedComments || []).map(c => c.ref)}
                        comments={comparisonMode ? [] : normalizedComments}
                        comparisonMode={comparisonMode}
                        citationHighlights={citationHighlights}
                        includeCitationHighlights={includeCitationHighlights}
                        forcedActiveTab={forcedActiveTab}
                        headerActions={headerActions}
                        processingBadge={processingBadge}
                        isAnalyzing={showOverlay}
                        activeDocumentSource={activeDocumentSource}
                        onDocumentSourceChange={onDocumentSourceChange}
                        referenceSource={referenceSource}
                        referenceNavigationRequest={referenceNavigationRequest}
                        comparisonVersionSource={comparisonVersionSource}
                        comparisonVersionNavigationRequest={comparisonVersionNavigationRequest}
                        contractNavigationRequest={contractNavigationRequest}
                    />

                    {showOverlay && (
                        <div className="premium-loader-overlay">

                            <div className="premium-loader-card">
                                <div className="premium-loader-card__aurora" aria-hidden="true" />
                                <div className="premium-loader-card__grid" aria-hidden="true" />

                                <div className="premium-loader-card__orb" aria-hidden="true">
                                    <span className="premium-loader-card__orb-ring premium-loader-card__orb-ring--1" />
                                    <span className="premium-loader-card__orb-ring premium-loader-card__orb-ring--2" />
                                    <span className="premium-loader-card__orb-ring premium-loader-card__orb-ring--3" />
                                    <span className="premium-loader-card__orb-core" />
                                </div>

                                <div className="premium-loader-card__content">
                                    <span className="premium-loader-card__eyebrow">
                                        Verdictia · AI
                                    </span>
                                    <span className="premium-loader-card__title">
                                        {isAnalyzing ? t('stage.analysis_in_progress') : t('stage.processing_contract')}
                                    </span>
                                    <span className="premium-loader-card__progress" aria-hidden="true">
                                        <span className="premium-loader-card__progress-bar" />
                                    </span>
                                </div>

                                <span className="premium-loader-card__shine" aria-hidden="true" />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default DocumentPanel;
